import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { X, Clock, Calendar, CheckSquare } from 'lucide-react';
import { format } from 'date-fns';
import { HistoryEvent } from '../utils/types';

interface HistoryDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  historyEvent: HistoryEvent;
}

const HistoryDetailModal: React.FC<HistoryDetailModalProps> = ({
  open,
  onOpenChange,
  historyEvent
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 pt-6 pb-2 sticky top-0 bg-background z-10 border-0">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base font-normal flex items-center gap-2 min-w-0 flex-1">
              {historyEvent.taskColor && (
                <span
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: historyEvent.taskColor }}
                />
              )}
              <span className="break-words min-w-0">{historyEvent.taskName}</span>
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 hover:bg-muted shrink-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto px-6 pt-2 pb-6 space-y-4">
          <div className="space-y-2">
            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-foreground">
                  {format(new Date(historyEvent.startDate), 'EEEE, MMMM d, yyyy')}
                </span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">
                  {format(new Date(historyEvent.startDate), 'h:mm a')} - {format(new Date(historyEvent.endDate), 'h:mm a')}
                </span>
              </div>

              {historyEvent.duration && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">
                    Duration: {historyEvent.duration}
                  </span>
                </div>
              )}

              {historyEvent.status && (
                <div className="flex items-center gap-2 text-sm">
                  <CheckSquare className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">
                    Status: {historyEvent.status}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default HistoryDetailModal;
